import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserX,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  User,
  ArrowLeft,
  Download,
  X,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  ShieldCheck,
} from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '../../../lib/queryClient';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import { toast } from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { get, post, patch, del } from '../../../api/apiClient';

import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import ResignationForm from '../../profile/ResignationForm';
import ExitRequestDetails from './ExitRequestDetails';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

const ExitRequests = ({ onBack, userRole = 'super_admin', initialMode, id, onClose, isDrawer }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, canUpdate } = usePermissions();
  const { user } = useAuth();

  const formatConfirmationMessage = (action, targetLabel) => {
    const subject =
      (targetLabel || 'this employee')
        .toString()
        .trim()
        .replace(/(.{35})/g, '$1\u200b'); // inject zero-width breaks for very long strings
    return `Are you sure you want to ${action} the exit request for:\n${subject}`;
  };

  const confirmAction = async (message) => {
    if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
      return true;
    }
    try {
      const response = window.confirm(message);
      if (response instanceof Promise) {
        return (await response) === true;
      }
      return response === true;
    } catch (err) {
      return false;
    }
  };

  const resolveUserEmail = () =>
    (user?.email ||
      user?.workEmail ||
      user?.personalEmail ||
      user?.contactInfo?.workEmail ||
      user?.contactInfo?.personalEmail ||
      user?.loginEmail ||
      '').toString().trim();

  const buildCurrentUserMeta = () => ({
    id: user?._id || user?.id || '507f1f77bcf86cd799439011',
    name: user?.name || user?.firstName || user?.personalInfo?.firstName || 'User',
    role: userRole || user?.role || 'super_admin',
    email: resolveUserEmail() || 'no-reply@exit-manager.com',
    department: user?.department || user?.employmentInfo?.department || '',
    employeeId: user?.employeeId || user?.employeeCode || ''
  });

  const normalizeId = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return normalizeId(
        value._id ||
        value.id ||
        value.employeeId ||
        value.employeeCode ||
        value.userId ||
        value.value ||
        value.uuid
      );
    }
    try {
      return value.toString();
    } catch (err) {
      return `${value}`;
    }
  };

  const normalizeEmail = (value) => {
    if (!value || typeof value !== 'string') return null;
    return value.trim().toLowerCase();
  };

  const currentUserIdentifiers = useMemo(() => {
    return [user?._id, user?.id, user?.employeeId, user?.employeeCode, user?.userId, user?.employeeProfileId]
      .map(normalizeId)
      .filter(Boolean);
  }, [user]);

  const currentUserEmails = useMemo(() => {
    return [
      user?.email,
      user?.workEmail,
      user?.personalEmail,
      user?.contactInfo?.workEmail,
      user?.contactInfo?.personalEmail,
      user?.loginEmail,
    ]
      .map(normalizeEmail)
      .filter(Boolean);
  }, [user]);

  const isOwnExitRequest = useCallback(
    (request) => {
      if (!request) {
        return false;
      }
      const requestIdentifiers = [
        request?.employeeId,
        request?.employeeId?._id,
        request?.employeeId?.id,
        request?.employeeId?.employeeId,
        request?.employeeId?.employeeCode,
        request?.employeeUserId,
        request?.createdBy,
        request?.createdBy?._id,
        request?.createdBy?.id,
        request?.createdById,
        request?.createdBy?.employeeId,
        request?.employeeProfileId,
      ]
        .map(normalizeId)
        .filter(Boolean);
      const identifiersMatch =
        currentUserIdentifiers.length > 0 &&
        requestIdentifiers.some((id) => currentUserIdentifiers.includes(id));

      if (identifiersMatch) {
        return true;
      }

      if (currentUserEmails.length === 0) {
        return false;
      }

      const requestEmails = [
        request?.employeeEmail,
        request?.employeeId?.email,
        request?.employeeId?.contactInfo?.workEmail,
        request?.employeeId?.contactInfo?.personalEmail,
        request?.personalEmail,
        request?.createdBy?.email,
        request?.createdByEmail,
      ]
        .map(normalizeEmail)
        .filter(Boolean);

      return requestEmails.some((email) => currentUserEmails.includes(email));
    },
    [currentUserIdentifiers, currentUserEmails]
  );
  // const [activeTab, setActiveTab] = useState('dashboard');
  const [showExitForm, setShowExitForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const isInitialFilterMount = useRef(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [departmentFilter, setDepartmentFilter] = useState(searchParams.get('department') || 'all');
  const [resignationTypeFilter, setResignationTypeFilter] = useState(searchParams.get('resignationType') || 'all');
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [departments, setDepartments] = useState([]);

  // ── Employee selector state (HR "New Request" flow) ──────────────────────
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);


  // Permission-based access control
  const canCreateRequest = hasPermission('exit_management', 'create');
  const canApproveRequest = hasPermission('exit_management', 'update');
  const canViewAllRequests = hasPermission('exit_management', 'read');
  const canEditRequest = hasPermission('exit_management', 'update');
  const canDeleteRequest = hasPermission('exit_management', 'delete');
  // Load departments from backend
  const loadDepartments = async () => {
    try {
      const result = await get('/hr/departments', {
        params: {
          page: 1,
          limit: 10,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          isActive: 'all',
          paginated: true,
        },
      });
      const raw = result?.data?.docs || result?.data?.departments || result?.data || result?.departments || result || [];
      const names = (Array.isArray(raw) ? raw : [])
        .map((d) => (typeof d === 'string' ? d : d?.name || d?.departmentName || d?.label || d?.value))
        .filter(Boolean);

      if (names.length) {
        setDepartments((prev) => Array.from(new Set([...(prev || []), ...names])));
      }
    } catch (err) {
    }
  };




  // Row Selection Handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Map();
      exitRequests.forEach(row => {
        newSelected.set(row._id || row.requestId, row);
      });
      setSelectedRows(newSelected);
    } else {
      setSelectedRows(new Map());
    }
  };

  const handleSelectRow = (row) => {
    const newSelected = new Map(selectedRows);
    const id = row._id || row.requestId;
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.set(id, row);
    }
    setSelectedRows(newSelected);
  };


  const handleExportSelected = async (format) => {
    setExportLoading(true);
    try {
      const ids = Array.from(selectedRows.keys());
      await requestBackendExport({ scope: 'selected', format, selectedIds: ids });
      toast.success('Selected rows export successful');
      setSelectedRows(new Map());
      setShowInlineSelectedFormats(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };



  const urlId = searchParams.get('id');
  const urlAction = searchParams.get('action');

  useEffect(() => {
    if (initialMode === 'create') {
      setShowExitForm(true);
    } else if (initialMode === 'view' && id) {
      if (!selectedRequest || String(selectedRequest._id) !== String(id)) {
        setSelectedRequest({ _id: id });
      }
    } else {
      // If we have an id but NO drawer action, show it inline
      if (urlId && urlAction !== 'viewExitCase') {
        if (!selectedRequest || String(selectedRequest._id) !== String(urlId)) {
          setSelectedRequest({ _id: urlId });
        }
      } else if (!urlId && selectedRequest && !initialMode) {
        setSelectedRequest(null);
      }
    }
  }, [initialMode, id, urlId, urlAction, selectedRequest]);

  // ── Load all employees for the HR "New Request" dropdown ─────────────────
  const loadEmployeeList = async () => {
    if (employeeList.length > 0) return; // already loaded
    setIsLoadingEmployees(true);
    try {
      const result = await get('/hr/employees', { params: { page: 1, limit: 200 } });
      const list = result?.data?.employees || [];
      setEmployeeList(list);
    } catch (err) {
      toast.error('Failed to load employee list');
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Filtered employees based on search text
  const filteredEmployees = employeeList.filter((emp) => {
    if (!employeeSearch.trim()) return true;
    const q = employeeSearch.toLowerCase();
    const name = `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.toLowerCase();
    const empId = (emp.employeeId || '').toLowerCase();
    const email = (emp.contactInfo?.personalEmail || emp.user?.email || '').toLowerCase();
    const dept = (emp.employmentInfo?.department || '').toLowerCase();
    return name.includes(q) || empId.includes(q) || email.includes(q) || dept.includes(q);
  });

  // Helper function for text input key press (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    const char = e.key;
    if (!/^[a-zA-Z0-9\s,]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {
      e.preventDefault();
    }
  };

  // Helper function for number input key down (positive numbers only, 0 and greater)
  const handleNumberKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period (for decimal)
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  // Helper function for phone number key press (digits only, max 10)
  const handlePhoneKeyPress = (e) => {
    const char = e.key;
    if (!/^[0-9]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab') {
      e.preventDefault();
    }
  };

  // Helper function for search input key press (alphanumeric, spaces, and email special characters)
  const handleSearchKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Debounce main search term for backend search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Pagination state — initialized from URL
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);

  // Reset pagination when filters change (skip initial mount to preserve URL page on refresh)
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, departmentFilter, resignationTypeFilter, startDate, endDate]);

  const [sortConfig, setSortConfig] = useState({
    key: searchParams.get('sortBy') || null,
    direction: searchParams.get('sortOrder') || 'asc',
  });

  // Sync filter/sort/pagination state to URL search params (persists on refresh)
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      const managedKeys = [
        'search',
        'status',
        'department',
        'resignationType',
        'startDate',
        'endDate',
        'page',
        'limit',
        'sortBy',
        'sortOrder',
      ];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (departmentFilter && departmentFilter !== 'all') params.set('department', departmentFilter);
      if (resignationTypeFilter && resignationTypeFilter !== 'all') params.set('resignationType', resignationTypeFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortConfig.key) params.set('sortBy', sortConfig.key);
      if (sortConfig.key && sortConfig.direction !== 'asc') params.set('sortOrder', sortConfig.direction);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    statusFilter,
    departmentFilter,
    resignationTypeFilter,
    startDate,
    endDate,
    currentPage,
    itemsPerPage,
    sortConfig,
    setSearchParams,
  ]);

  // Export State
  const [selectedRows, setSelectedRows] = useState(new Map());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const baseExportFilters = useMemo(() => ({
    search: debouncedSearchTerm,
    status: statusFilter,
    department: departmentFilter,
    resignationType: resignationTypeFilter,
    startDate,
    endDate,
  }), [debouncedSearchTerm, statusFilter, departmentFilter, resignationTypeFilter, startDate, endDate]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const payload = {
        module: 'exit-requests',
        format,
        scope,
        filters: overrideFilters ?? baseExportFilters,
        page: overridePage ?? 1,
        limit: overrideLimit ?? 100,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        selectedIds: selectedIds ?? [],
      };

      const blob = await post('/exports/generate', payload, { responseType: 'blob' });
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const filename = `exit-requests-export.${fallbackExt}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters]
  );

  // Close inline format bar when selection is cleared
  useEffect(() => {
    if (selectedRows && selectedRows.size === 0) setShowInlineSelectedFormats(false);
  }, [selectedRows?.size]);

  // Animate panel in
  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  // Click-outside closes the panel
  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (selectedExportWrapRef.current && !selectedExportWrapRef.current.contains(e.target)) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleExportCurrentPage = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({ 
        scope: 'currentPage', 
        format, 
        overridePage: currentPage,
        overrideLimit: itemsPerPage 
      });
      toast.success('Current page export successful');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportFiltered = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({ scope: 'filtered', format, overrideLimit: 1000 });
      toast.success('Filtered export successful');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportRange = async (rangeStartDate, rangeEndDate, format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'dateRange',
        format,
        overrideLimit: 1000,
        overrideFilters: { ...baseExportFilters, startDate: rangeStartDate, endDate: rangeEndDate }
      });
      toast.success('Date range export successful');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportAll = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'all',
        format,
        overrideFilters: {},
        overrideLimit: 1000,
      });
      toast.success('All data export successful');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  // React Query: Fetch exit requests with backend filters
  const {
    data: exitRequestsData,
    isLoading: isLoadingRequests,
    refetch: refetchExitRequests,
  } = useQuery({
    queryKey: ['exitRequests', debouncedSearchTerm, statusFilter, departmentFilter, resignationTypeFilter, startDate, endDate, currentPage, itemsPerPage],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (departmentFilter && departmentFilter !== 'all') params.department = departmentFilter;
      if (resignationTypeFilter && resignationTypeFilter !== 'all') params.resignationType = resignationTypeFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const result = await get('/exit', { params });
      const requests = result.data?.exitRequests || result.data || [];

      // Extract pagination data from the correct structure
      const pagination = result.data?.pagination || {};
      const total = pagination.totalItems || result.data?.totalCount || result.totalCount || requests.length;
      const pages = pagination.totalPages || result.data?.totalPages || result.totalPages || Math.ceil(total / itemsPerPage);

      // Extract departments from requests
      const deptSet = new Set(requests.map(req => req.department).filter(Boolean));
      setDepartments((prev) => Array.from(new Set([...(prev || []), ...deptSet])));

      return {
        exitRequests: requests,
        totalCount: total,
        totalPages: pages
      };
    },
    refetchInterval: 0,
  });

  const exitRequests = exitRequestsData?.exitRequests || [];
  const totalCount = exitRequestsData?.totalCount || 0;
  const totalPages = exitRequestsData?.totalPages || 1;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedExitRequests = useMemo(() => {
    if (!sortConfig.key) return exitRequests;

    return [...exitRequests].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [exitRequests, sortConfig]);

  // React Query: Fetch exit statistics
  const {
    data: exitStatsData,
    refetch: refetchExitStats,
  } = useQuery({
    queryKey: ['exitStats'],
    queryFn: async () => {
      const result = await get('/exit/stats/dashboard');
      return result.data || {};
    },
    refetchInterval: 0,
  });

  const exitStats = exitStatsData || {};

  useEffect(() => {
    loadDepartments();
  }, []);

  // React Query: Approve exit request mutation
  const approveExitRequestMutation = useMutation({
    mutationFn: async ({ requestId, approvalRole, approvalReason, comments, currentUser }) => {
      return patch(`/exit/${requestId}/approve`, {
        comments: comments || `Approved by ${currentUser.name}`,
        managerId: currentUser.id,
        managerEmployeeId: currentUser.employeeId || '',
        managerName: currentUser.name,
        managerEmail: currentUser.email,
        managerDepartment: currentUser.department || '',
        approvalRole: approvalRole || 'manager',
        approvalReason: approvalReason || comments || `Approved by ${currentUser.name}`,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['exitRequests'] });
      queryClient.invalidateQueries({ queryKey: ['exitStats'] });
      toast.success(`Exit request approved successfully by ${variables.currentUser.name}!`);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to approve exit request');
    },
  });


  // Approve exit request
  const handleApproveExit = (request, role = 'manager', reason = '') => {
    if (!request?._id) return;

    const currentUser = buildCurrentUserMeta();
    approveExitRequestMutation.mutate({
      requestId: request._id,
      currentUser,
      approvalRole: role,
      approvalReason: reason,
      comments: reason
    });
  };

  // React Query: Reject exit request mutation
  const rejectExitRequestMutation = useMutation({
    mutationFn: async ({ requestId, approvalRole, rejectedReason, comments, currentUser }) => {
      return patch(`/exit/${requestId}/reject`, {
        rejectedReason: rejectedReason || comments || `Rejected by ${currentUser.name}`,
        managerId: currentUser.id,
        managerEmployeeId: currentUser.employeeId || '',
        managerName: currentUser.name,
        managerEmail: currentUser.email,
        managerDepartment: currentUser.department || '',
        approvalRole: approvalRole || 'manager',
        comments: rejectedReason || comments || `Rejected by ${currentUser.name}`,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['exitRequests'] });
      queryClient.invalidateQueries({ queryKey: ['exitStats'] });
      toast.success(`Exit request rejected successfully by ${variables.currentUser.name}!`);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reject exit request');
    },
  });

  // Reject exit request
  const handleRejectExit = (request, role = 'manager', reason = '') => {
    if (!request?._id) return;

    const currentUser = buildCurrentUserMeta();
    rejectExitRequestMutation.mutate({
      requestId: request._id,
      currentUser,
      approvalRole: role,
      rejectedReason: reason,
      comments: reason
    });
  };

  // React Query: Delete exit request mutation
  const deleteExitRequestMutation = useMutation({
    mutationFn: async (requestId) => {
      return del(`/exit/${requestId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exitRequests'] });
      queryClient.invalidateQueries({ queryKey: ['exitStats'] });
      toast.success('Exit request deleted successfully!');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete exit request');
    },
  });

  // React Query: Allow/Disallow Withdrawal mutation
  const allowWithdrawalMutation = useMutation({
    mutationFn: async ({ requestId, allowWithdrawal }) => {
      return patch(`/exit/${requestId}/allow-withdrawal`, { allowWithdrawal });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['exitRequests'] });
      queryClient.invalidateQueries({ queryKey: ['exitRequest', data.data?._id] });
      toast.success(data.responseMessage || data.message || 'Withdrawal status updated successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const handleAllowWithdrawal = (request, isForce = false) => {
    if (!request?._id) return;
    if (isForce) {
      toast.error('Force withdrawal is not implemented in this flow as per requirements.');
      return;
    }
    allowWithdrawalMutation.mutate({ requestId: request._id, allowWithdrawal: true });
  };

  // Delete exit request with confirmation
  // IMPORTANT: This function MUST show confirmation BEFORE making any API call
  const handleDeleteExit = async (e, requestId) => {
    // Stop all event propagation immediately to prevent any other handlers
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation && typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
    }

    // Prevent if already deleting
    if (deleteExitRequestMutation.isPending) {
      return;
    }

    const userConfirmed = await confirmAction('Are you sure you want to delete this exit request? This action cannot be undone.');

    if (userConfirmed !== true) {
      return;
    }
    deleteExitRequestMutation.mutate(requestId);
  };

  // Bulk delete exit requests handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    const userConfirmed = await confirmAction(`Are you sure you want to delete ${selectedRows.size} exit request(s)? This action cannot be undone.`);
    if (userConfirmed !== true) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedRows.keys());
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process deletions in parallel batches of 5 to speed up but not overwhelm
      const batchSize = 5;
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        await Promise.all(batch.map(async (id) => {
          try {
            await del(`/exit/${id}`);
            successCount++;
          } catch (err) {
            errorCount++;
          }
        }));
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} request(s)`);
        setSelectedRows(new Map());
        queryClient.invalidateQueries({ queryKey: ['exitRequests'] });
        queryClient.invalidateQueries({ queryKey: ['exitStats'] });
      }

      if (errorCount > 0) {
        toast.error(`Failed to delete ${errorCount} request(s). Please try again.`);
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // React Query: Update exit request status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ requestId, newStatus, currentUser }) => {
      return patch(`/exit/${requestId}/status`, {
        status: newStatus,
        updatedBy: currentUser.id,
        updatedByName: currentUser.name,
        comments: `Status updated to ${newStatus} by ${currentUser.name}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exitRequests'] });
      queryClient.invalidateQueries({ queryKey: ['exitStats'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update exit request status');
    },
  });

  // Update exit request status
  const handleStatusUpdate = (requestId, newStatus) => {
    const currentUser = {
      id: user?._id || user?.id || '507f1f77bcf86cd799439011',
      name: user?.name || user?.firstName || 'User',
      role: userRole || user?.role || 'super_admin'
    };
    updateStatusMutation.mutate({ requestId, newStatus, currentUser });
  };


  // Frontend filtering removed - now handled by backend

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      withdrawn: 'bg-gray-100 text-gray-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="h-4 w-4" />,
      approved: <CheckCircle className="h-4 w-4" />,
      rejected: <XCircle className="h-4 w-4" />,
      withdrawn: <AlertCircle className="h-4 w-4" />,
      completed: <CheckCircle className="h-4 w-4" />
    };
    return icons[status] || <AlertCircle className="h-4 w-4" />;
  };

  const renderApprovalBadge = (status) => {
    const s = status?.toLowerCase() || 'pending';
    const styles = {
      approved: 'text-emerald-600 bg-emerald-50 border-emerald-100',
      rejected: 'text-rose-600 bg-rose-50 border-rose-100',
      pending: 'text-amber-600 bg-amber-50 border-amber-100'
    };
    const style = styles[s] || styles.pending;
    return (
      <span className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${style}`}>
        {s}
      </span>
    );
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setResignationTypeFilter('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
  };

  // Render requests list
  const renderRequestsList = () => (
    <div className="space-y-6">
      {/* KPI Cards - Moved from Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserX className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Exit Requests</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(exitStats.totalRequests || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(exitStats.pendingRequests || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(exitStats.approvedRequests || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(exitStats.rejectedRequests || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search Bar */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Exit ID, name, or email..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={handleSearchKeyPress}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  // Maintain focus on input
                  const input = document.activeElement;
                  if (input && input.tagName === 'INPUT') {
                    setTimeout(() => input.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="withdrawed">Withdrawed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Department Filter */}
          <div className="flex-shrink-0 w-full sm:w-auto max-w-[240px]">
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          {/* Resignation Type Filter */}
          <div className="flex-shrink-0">
            <select
              value={resignationTypeFilter}
              onChange={(e) => setResignationTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Types</option>
              <option value="voluntary">Voluntary</option>
              <option value="involuntary">Involuntary</option>
              <option value="retirement">Retirement</option>
              <option value="contract_end">Contract End</option>
            </select>
          </div>

          {/* Submitted On Date Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
              }}
              containerClassName="flex items-center space-x-2"
            />
          </div>

          {/* Refresh & Clear Filters */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {(searchTerm || statusFilter !== 'all' || departmentFilter !== 'all' || resignationTypeFilter !== 'all' || startDate || endDate) && (
              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-100"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Exit Requests</h3>
        </div>

        {isLoadingRequests ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading...</span>
          </div>
        ) : exitRequests.length === 0 ? (
          <div className="text-center py-8">
            <UserX className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No exit requests found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={exitRequests.length > 0 && selectedRows.size === exitRequests.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('requestId')}>
                    <div className="flex items-center space-x-1">
                      <span>Exit Id</span>
                      {sortConfig.key === 'requestId' ? (
                        sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('employeeName')}>
                    <div className="flex items-center space-x-1">
                      <span>Employee</span>
                      {sortConfig.key === 'employeeName' ? (
                        sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('department')}>
                    <div className="flex items-center space-x-1">
                      <span>Department</span>
                      {sortConfig.key === 'department' ? (
                        sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenure (Join & LWD)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notice End
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proposed LWD
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shortfall
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyout
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manager
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortConfig.key === 'status' ? (
                        sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdAt')}>
                    <div className="flex items-center space-x-1">
                      <span>Submitted On</span>
                      {sortConfig.key === 'createdAt' ? (
                        sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedExitRequests.map((request) => (
                  <tr key={request._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedRows.has(request._id || request.requestId)}
                        onChange={() => handleSelectRow(request)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-agrosaf-darkGreen">
                        {request.requestId || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap max-w-[240px]">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 capitalize truncate max-w-[180px]" title={request.employeeName || 'N/A'}>
                            {request.employeeName}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-[220px]" title={request.employeeEmail || 'N/A'}>
                            {request.employeeEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{request.department}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[11px] text-gray-900 font-medium">
                        Join: {formatDate(request.employeeId?.employmentInfo?.joinDate || request.joinDate, { includeTime: false })}
                      </div>
                      <div className="text-[11px] text-blue-600 font-bold mt-1">
                        LWD: {formatDate(request.lastWorkingDate, { includeTime: false })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                      {formatDate(request.noticeEndDate, { includeTime: false })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                      {formatDate(request.proposedLastWorkingDate, { includeTime: false })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {request.shortfall > 0 ? (
                        <span className="text-sm text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded border border-red-100">
                          {request.shortfall} Days
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">0 Days</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-xs font-bold px-2 py-1 rounded-lg ${request.buyoutRequest === 'Yes' ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-gray-100 text-gray-500'}`}>
                        {request.buyoutRequest || 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderApprovalBadge(request.managerApproval?.approvalStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderApprovalBadge(request.hrApproval?.approvalStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold shadow-sm border ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1.5 capitalize">{request.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 font-medium">
                      {formatDate(request.createdAt, { includeTime: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <PermissionGate permission="exit_management" action="read">
                          <button
                            onClick={() => {
                              if (isDrawer) {
                                setSelectedRequest(request);
                              } else {
                                setSearchParams(prev => {
                                  prev.set('id', request._id);
                                  return prev;
                                });
                                setSelectedRequest(request);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>

                        <PermissionGate permission="exit_management" action="delete">
                          <button
                            onClick={(e) => {
                              // CRITICAL: Stop all event propagation FIRST
                              e.preventDefault();
                              e.stopPropagation();
                              e.stopImmediatePropagation?.();

                              // CRITICAL: Call handler which will show confirmation BEFORE API call
                              handleDeleteExit(e, request._id);
                            }}
                            disabled={deleteExitRequestMutation.isPending}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            title="Delete"
                            type="button"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Pagination Controls */}
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              limit={itemsPerPage}
              hasNextPage={currentPage < totalPages}
              hasPrevPage={currentPage > 1}
              onPageChange={setCurrentPage}
              onLimitChange={(newLimit) => {
                setItemsPerPage(newLimit);
                setCurrentPage(1);
              }}
              limitOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );

  if (selectedRequest) {
    return (
      <div className="min-h-screen">
        <ExitRequestDetails
          requestId={selectedRequest._id}
          onBack={() => {
            if (isDrawer && onClose) {
              onClose();
            } else {
              setSearchParams(prev => {
                prev.delete('id');
                return prev;
              });
              setSelectedRequest(null);
            }
          }}
          hideBack={isDrawer}
          onApprove={(req, role, reason) => handleApproveExit(req, role, reason)}
          onReject={(req, role, reason) => handleRejectExit(req, role, reason)}
          onWithdraw={(req, isForce) => handleAllowWithdrawal(req, isForce)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Exit Requests</h1>
            <p className="text-gray-600">Manage employee resignation requests and exit process</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <PermissionGate permission="exit_management" action="delete">
            {selectedRows.size > 0 && (
              <Button
                variant="danger"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white"
              >
                {isBulkDeleting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>Delete Selected ({selectedRows.size})</span>
              </Button>
            )}
          </PermissionGate>

          <PermissionGate permission="exit_management" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant={selectedRows.size > 0 ? "primary" : "outline"}
                onClick={handleExportClick}
                className="flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}</span>
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleExportSelected}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="exit_management" action="create">
            <button
              onClick={() => setShowExitForm(true)}
              className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-md hover:bg-agrosaf-darkGreen focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Request
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Exit Requests"
        scopedFormatExport={{
          onExport: (scope, format, meta) => {
            if (scope === 'currentPage') return handleExportCurrentPage(format);
            if (scope === 'filtered') return handleExportFiltered(format);
            if (scope === 'dateRange') return handleExportRange(meta.startDate, meta.endDate, format);
            if (scope === 'all') return handleExportAll(format);
          }
        }}
      />

      {/* Content */}
      {showExitForm ? (
        <div className="space-y-4">
          {/* ── Employee Selector ──────────────────────────────────────── */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">New Exit Request</h3>
                <p className="text-sm text-gray-500 mt-0.5">Select an employee to auto-fill their details</p>
              </div>
              <button
                onClick={() => {
                  setShowExitForm(false);
                  setSelectedEmployee(null);
                  setEmployeeSearch('');
                  setShowEmployeeDropdown(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Select Employee <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                {/* Input / selected pill */}
                <div
                  className="flex items-center gap-2 w-full px-3 py-2 border border-gray-300 rounded-lg cursor-text focus-within:ring-2 focus-within:ring-agrosaf-green focus-within:border-agrosaf-green bg-white"
                  onClick={() => {
                    loadEmployeeList();
                    setShowEmployeeDropdown(true);
                  }}
                >
                  {selectedEmployee ? (
                    <div className="flex items-center gap-2 flex-1 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-800 text-sm font-medium px-2.5 py-1 rounded-full">
                        <User className="h-3.5 w-3.5" />
                        {selectedEmployee.personalInfo?.firstName} {selectedEmployee.personalInfo?.lastName}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEmployee(null);
                            setEmployeeSearch('');
                          }}
                          className="ml-1 text-green-600 hover:text-green-900"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                      <span className="text-xs text-gray-500">{selectedEmployee.employeeId}</span>
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="Search by name, ID, email or department..."
                      value={employeeSearch}
                      onChange={(e) => {
                        setEmployeeSearch(e.target.value);
                        setShowEmployeeDropdown(true);
                        loadEmployeeList();
                      }}
                      onFocus={() => {
                        loadEmployeeList();
                        setShowEmployeeDropdown(true);
                      }}
                      className="flex-1 outline-none text-sm bg-transparent placeholder-gray-400"
                    />
                  )}
                  {isLoadingEmployees && (
                    <RefreshCw className="h-4 w-4 animate-spin text-gray-400 flex-shrink-0" />
                  )}
                </div>

                {/* Dropdown list */}
                {showEmployeeDropdown && !selectedEmployee && (
                  <div
                    className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto"
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {isLoadingEmployees ? (
                      <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        Loading employees...
                      </div>
                    ) : filteredEmployees.length === 0 ? (
                      <div className="py-6 text-center text-gray-400 text-sm">No employees found</div>
                    ) : (
                      filteredEmployees.map((emp) => {
                        const fullName = `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim();
                        const email = emp.contactInfo?.personalEmail || emp.user?.email || '';
                        const dept = emp.employmentInfo?.department || '';
                        const empId = emp.employeeId || '';
                        return (
                          <button
                            key={emp._id}
                            type="button"
                            onClick={() => {
                              setSelectedEmployee(emp);
                              setShowEmployeeDropdown(false);
                              setEmployeeSearch('');
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-8 h-8 rounded-full bg-agrosaf-green/10 flex items-center justify-center flex-shrink-0">
                                  <User className="h-4 w-4 text-agrosaf-green" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">{fullName || 'Unknown'}</p>
                                  <p className="text-xs text-gray-500 truncate">{email || 'No email'}</p>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <span className="inline-block text-xs font-mono font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{empId}</span>
                                {dept && (
                                  <p className="text-xs text-gray-400 mt-0.5">{dept}</p>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Selected employee info card */}
              {selectedEmployee && (
                <div className="mt-4 p-4 bg-green-50 border border-green-100 rounded-lg">
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">Selected Employee Details</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">Full Name</p>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedEmployee.personalInfo?.firstName} {selectedEmployee.personalInfo?.lastName}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Employee ID</p>
                      <p className="text-sm font-mono font-bold text-gray-900">{selectedEmployee.employeeId || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm text-gray-900 truncate">{selectedEmployee.user?.email || selectedEmployee.contactInfo?.personalEmail || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Department</p>
                      <p className="text-sm font-medium text-gray-900">{selectedEmployee.employmentInfo?.department || '—'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Resignation Form (unchanged) ────────────────────────────── */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-base font-medium text-gray-900">Resignation Details</h4>
              {!selectedEmployee && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Please select an employee above to proceed
                </p>
              )}
            </div>
            <ResignationForm
              requests={[]}
              onBehalfOf={selectedEmployee}
              onSuccess={() => {
                setShowExitForm(false);
                setSelectedEmployee(null);
                setEmployeeSearch('');
                queryClient.invalidateQueries({ queryKey: ['exitRequests'] });
                queryClient.invalidateQueries({ queryKey: ['exitStats'] });
              }}
            />
          </div>
        </div>
      ) : renderRequestsList()}
    </div>
  );
};

export default ExitRequests;